import { Button, DialogContent, DialogTitle, Stack, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { WorkflowType } from "@entrilia/shared/api/workflowTypes";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import CancelDialogIcon from "@entrilia/shared/icons/CancelDialogIcon";
import { logError } from "@entrilia/shared/logging";
import { workflowTypeCaptionMap } from "@entrilia/shared/utilities/enumCaptions";
import adminApi from "../../../../../api/adminApi";

interface Props {
  workflowId: string;
  workflowType: WorkflowType;
  onClose: () => void;
  onCancelled: () => void;
}

const cancelWorkflow = withErrorHandling(adminApi.cancelWorkflow);

const CancelWorkflowDialog = ({ workflowId, workflowType, onClose, onCancelled }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [cancelling, setCancelling] = useState(false);
  const [comment, setComment] = useState("");

  const handleCancelWorkflow = async () => {
    const commentUserInput = comment.trim();
    if (!commentUserInput) {
      return;
    }

    setCancelling(true);
    const [resp, error] = await cancelWorkflow(workflowId, { commentUserInput });
    setCancelling(false);

    if (error) {
      logError(error, "[CancelWorkflowDialog] cancelWorkflow");
      sendNotificationError(error.message);
      return;
    }

    if (!resp.isSuccess) {
      const failureMessages = (resp.preconditionFailures ?? []).map((failure) => failure.message).join("\n");
      sendNotificationError(failureMessages || resp.errorMessage || "Workflow could not be cancelled");
      return;
    }

    sendNotification("Workflow cancelled successfully");
    onCancelled();
    onClose();
  };

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>Cancel</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <CancelDialogIcon sx={{ width: 80, height: 80 }} />

          <Typography variant="h6" sx={{ textAlign: "center" }}>
            Cancel {workflowTypeCaptionMap[workflowType] ?? workflowType}?
          </Typography>

          <Typography color="textSecondary" sx={{ textAlign: "center" }}>
            You&apos;re about to cancel this workflow run. This action is permanent and can&apos;t be undone.
          </Typography>

          <TextField
            multiline
            rows={4}
            spellCheck
            fullWidth
            placeholder="Add cancellation reason..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose} cancelText="Close">
        <Button
          color="error"
          variant="outlined"
          loading={cancelling}
          disabled={!comment.trim()}
          onClick={handleCancelWorkflow}
        >
          Cancel Run
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default CancelWorkflowDialog;
